import { useCallback, useEffect, useState } from "react";

import ExecutorApp from "../services/ExecutorApp";
import { useTypedSelector } from "../redux/store";

export const GPS_SPOOFING_TRANSLATION_PATH = "gpsSpoofingButton";

/**
 * Drivers' GPS spoofing state for the dispatcher's taxi services. The button is
 * granted by a role permission and is disabled while the «GPS spoofing» display
 * is off in the driver app settings of all those taxi services. The state lives
 * on the server, so every button (orders, executors) shows the same value: it
 * is loaded on mount and refreshed on every switch or settings change.
 */
function useGpsSpoofing() {
	const { connected, authorized } = useTypedSelector((state) => state.prpc);

	const [state, setState] = useState<ExecutorApp.GpsSpoofingState | null>(
		null,
	);
	const [pending, setPending] = useState(false);

	const load = useCallback(async () => {
		const res = await ExecutorApp.getGpsSpoofing();
		if (res) setState(res);
	}, []);

	useEffect(() => {
		if (!connected || !authorized) return undefined;

		let disposed = false;
		let unsubscribe: (() => void) | undefined;

		load();
		ExecutorApp.subscribeGpsSpoofing(load)
			.then((subscription) => {
				if (disposed) subscription.unsubscribe();
				else unsubscribe = subscription.unsubscribe;
			})
			.catch(() => undefined);

		return () => {
			disposed = true;
			unsubscribe?.();
		};
	}, [authorized, connected, load]);

	const loaded = state !== null;
	const items = state?.items ?? [];
	/** The display is on for drivers of at least one taxi service. */
	const available = items.length > 0;
	const active = items.some((item) => item.active);
	/** Key under GPS_SPOOFING_TRANSLATION_PATH for the button's hint. */
	let hintKey = active ? "disable" : "enable";
	if (loaded && !available) hintKey = "unavailable";

	const toggle = useCallback(async () => {
		if (pending || !available) return;
		setPending(true);
		const res = await ExecutorApp.setGpsSpoofing(!active);
		if (res) setState(res);
		setPending(false);
	}, [active, available, pending]);

	return {
		active,
		hintKey,
		disabled: pending || !loaded || !available,
		toggle,
	};
}

export default useGpsSpoofing;
